// "Install App": add Zuzu to the home screen as an app (a PWA).
// Android browsers fire `beforeinstallprompt` once, early, so it is caught here
// as soon as the app starts and kept until someone taps "Install App".
// iPhone and iPad have no such prompt: there, "Add to Home Screen" in the Share
// menu is the only way, so the button shows a short guide instead.
//
// The installed app always gets the latest Zuzu: it opens the live site, and the
// service worker loads pages from the network first (see public/service-worker.js).

import { useSyncExternalStore } from 'react';

type InstallPromptEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };

let deferred: InstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // keep it for our own button instead of the browser's mini bar
    deferred = e as InstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => { installed = true; deferred = null; emit(); });
}

// Already running as the installed app (Android, desktop, or iOS home screen).
export const isStandalone = () =>
  typeof window !== 'undefined' && (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );

// iPhone and iPad, including iPads that say they are a Mac.
export const isIOS = () =>
  typeof navigator !== 'undefined' &&
  (/iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

export type InstallState = 'installed' | 'prompt' | 'ios' | 'manual';

const snapshot = (): InstallState =>
  installed || isStandalone() ? 'installed' : deferred ? 'prompt' : isIOS() ? 'ios' : 'manual';

export const useInstallState = () => useSyncExternalStore(
  (l) => { listeners.add(l); return () => listeners.delete(l); },
  snapshot,
  () => 'manual' as InstallState,
);

// Chrome offers the install a few seconds after the page loads (after a first
// tap), so a quick tap on "Install App" can come before it. Wait for it, but only
// up to 4 seconds: the browser lets a page open its install dialog only shortly
// after a tap.
export const waitForPrompt = (ms = 4000): Promise<boolean> => {
  if (deferred) return Promise.resolve(true);
  return new Promise(resolve => {
    const done = (ok: boolean) => { clearTimeout(timer); listeners.delete(check); resolve(ok); };
    const check = () => { if (deferred) done(true); };
    const timer = setTimeout(() => done(false), ms);
    listeners.add(check);
  });
};

// Opens the browser's own install dialog (Android). Returns true if installed.
export const promptInstall = async (): Promise<boolean> => {
  const e = deferred;
  if (!e) return false;
  deferred = null;
  emit();
  await e.prompt();
  const { outcome } = await e.userChoice;
  if (outcome === 'accepted') { installed = true; emit(); }
  return outcome === 'accepted';
};
